import React, { useCallback, useEffect, useState } from 'react';
import { Shield, ShieldOff, Pencil, Check, X, RefreshCw, CheckCircle, Circle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database';

type ProfileRow = Database['public']['Tables']['profiles']['Row'];

interface UserRow extends ProfileRow {
  email?: string;
}

type TrainingBalanceRow = Database['public']['Functions']['current_training_balance']['Returns'][number];

const pickTrainingBalanceRow = (data: unknown): TrainingBalanceRow | null => {
  if (data == null) return null;
  if (Array.isArray(data)) {
    const row = data[0];
    return row && typeof row === 'object' ? (row as TrainingBalanceRow) : null;
  }
  if (typeof data === 'object') return data as TrainingBalanceRow;
  return null;
};

const UsersAdmin: React.FC = () => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [balances, setBalances] = useState<Record<string, number>>({});
  const [grantHours, setGrantHours] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [roleInput, setRoleInput] = useState('');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .order('name');
      if (fetchError) throw fetchError;
      const nextUsers = data ?? [];
      setUsers(nextUsers);
      setGrantHours((prev) => {
        const next: Record<string, string> = {};
        for (const user of nextUsers) {
          next[user.id] = prev[user.id] ?? '1';
        }
        return next;
      });

      const balanceEntries = await Promise.all(
        nextUsers.map(async (user) => {
          const { data: balanceData, error: balanceRpcError } = await supabase.rpc('current_training_balance', {
            p_user_id: user.id,
          });
          if (balanceRpcError) throw balanceRpcError;
          const balance = pickTrainingBalanceRow(balanceData);
          return [user.id, Number(balance?.remaining_hours ?? 0)] as const;
        }),
      );
      setBalances(Object.fromEntries(balanceEntries));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    let mounted = true;
    if (!supabase) return;
    void supabase.auth.getUser().then(({ data }) => {
      if (!mounted) return;
      setCurrentUserId(data.user?.id ?? null);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const toggleAdmin = async (user: UserRow) => {
    if (!supabase) return;
    setSaving(user.id);
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ is_admin: !user.is_admin })
        .eq('id', user.id);
      if (updateError) throw updateError;
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, is_admin: !u.is_admin } : u)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update admin status');
    } finally {
      setSaving(null);
    }
  };

  const toggleComplimentaryAccess = async (user: UserRow) => {
    if (!supabase) return;
    const next = !user.training_access_granted;
    setSaving(`complimentary-${user.id}`);
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ training_access_granted: next })
        .eq('id', user.id);
      if (updateError) throw updateError;
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, training_access_granted: next } : u)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update complimentary access');
    } finally {
      setSaving(null);
    }
  };

  const startEditRole = (user: UserRow) => {
    setEditingRoleId(user.id);
    setRoleInput(user.role);
  };

  const cancelEditRole = () => {
    setEditingRoleId(null);
    setRoleInput('');
  };

  const saveRole = async (userId: string) => {
    if (!supabase) return;
    setSaving(userId);
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ role: roleInput })
        .eq('id', userId);
      if (updateError) throw updateError;
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, role: roleInput } : u)),
      );
      setEditingRoleId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setSaving(null);
    }
  };

  const grantTrainingHours = async (userId: string) => {
    if (!supabase || !currentUserId) return;
    const requested = Number(grantHours[userId] ?? '0');
    if (!Number.isFinite(requested) || requested <= 0) {
      setError('Grant hours must be greater than zero.');
      return;
    }
    const wholeHours = Math.floor(requested);
    setSaving(`grant-${userId}`);
    setError(null);
    try {
      const { error: insertError } = await supabase.from('user_training_hour_grants').insert({
        user_id: userId,
        source: 'admin_grant',
        granted_hours: wholeHours,
        granted_by: currentUserId,
      });
      if (insertError) throw insertError;
      setBalances((prev) => ({
        ...prev,
        [userId]: (prev[userId] ?? 0) + wholeHours,
      }));
      setGrantHours((prev) => ({ ...prev, [userId]: '1' }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to grant training hours');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#1A1A1A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-[#1A1A1A]">
          {users.length} {users.length === 1 ? 'user' : 'users'}
        </h2>
        <button
          onClick={() => void fetchUsers()}
          className="flex items-center gap-2 text-sm text-[#555555] hover:text-[#1A1A1A] transition-colors"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white border border-[#DADADA] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#DADADA] bg-[#FAFAFA]">
              <th className="text-left px-4 py-3 font-medium text-[#555555]">Name</th>
              <th className="text-left px-4 py-3 font-medium text-[#555555]">Role / Title</th>
              <th className="text-center px-4 py-3 font-medium text-[#555555]">Level</th>
              <th className="text-center px-4 py-3 font-medium text-[#555555]">Admin</th>
              <th
                className="text-center px-4 py-3 font-medium text-[#555555]"
                title="Allows Kip training without a subscription (no monthly hours required)."
              >
                Complimentary access
              </th>
              <th className="text-center px-4 py-3 font-medium text-[#555555]">Hours left (month)</th>
              <th className="text-left px-4 py-3 font-medium text-[#555555]">Grant free hours</th>
              <th className="text-left px-4 py-3 font-medium text-[#555555]">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#DADADA]">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-[#FAFAFA] transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium text-[#1A1A1A]">{user.name}</div>
                  <div className="text-xs text-[#888888] font-mono truncate max-w-[200px]">{user.id}</div>
                </td>
                <td className="px-4 py-3">
                  {editingRoleId === user.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        value={roleInput}
                        onChange={(e) => setRoleInput(e.target.value)}
                        className="border border-[#DADADA] rounded px-2 py-1 text-sm flex-1 focus:outline-none focus:border-[#1A1A1A]"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void saveRole(user.id);
                          if (e.key === 'Escape') cancelEditRole();
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => void saveRole(user.id)}
                        disabled={saving === user.id}
                        className="text-green-600 hover:text-green-700 disabled:opacity-50"
                        aria-label="Save role"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={cancelEditRole}
                        className="text-[#888888] hover:text-[#1A1A1A]"
                        aria-label="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 group">
                      <span className="text-[#555555]">{user.role}</span>
                      <button
                        onClick={() => startEditRole(user)}
                        className="opacity-0 group-hover:opacity-100 text-[#888888] hover:text-[#1A1A1A] transition-opacity"
                        aria-label="Edit role"
                      >
                        <Pencil size={13} />
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-center text-[#555555]">{user.level}</td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => void toggleAdmin(user)}
                    disabled={saving === user.id}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors disabled:opacity-50 ${
                      user.is_admin
                        ? 'bg-[#1A1A1A] text-white hover:bg-[#333333]'
                        : 'bg-[#F5F5F5] text-[#555555] hover:bg-[#EBEBEB]'
                    }`}
                    aria-label={user.is_admin ? 'Revoke admin' : 'Grant admin'}
                  >
                    {user.is_admin ? <Shield size={12} /> : <ShieldOff size={12} />}
                    {user.is_admin ? 'Admin' : 'User'}
                  </button>
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    type="button"
                    onClick={() => void toggleComplimentaryAccess(user)}
                    disabled={saving === `complimentary-${user.id}`}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors disabled:opacity-50 ${
                      user.training_access_granted
                        ? 'bg-emerald-700 text-white hover:bg-emerald-800'
                        : 'bg-[#F5F5F5] text-[#555555] hover:bg-[#EBEBEB]'
                    }`}
                    title="Grant or revoke Kip access without a subscription."
                    aria-label={
                      user.training_access_granted ? 'Revoke complimentary training access' : 'Grant complimentary training access'
                    }
                  >
                    {user.training_access_granted ? <CheckCircle size={12} /> : <Circle size={12} />}
                    {user.training_access_granted ? 'On' : 'Off'}
                  </button>
                </td>
                <td className="px-4 py-3 text-center text-[#555555]">
                  {(balances[user.id] ?? 0).toFixed(2)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      step={1}
                      value={grantHours[user.id] ?? '1'}
                      onChange={(event) =>
                        setGrantHours((prev) => ({ ...prev, [user.id]: event.target.value }))
                      }
                      className="w-20 border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A]"
                    />
                    <button
                      onClick={() => void grantTrainingHours(user.id)}
                      disabled={saving === `grant-${user.id}` || !currentUserId}
                      className="inline-flex items-center rounded-md border border-[#DADADA] px-2.5 py-1.5 text-xs font-medium text-[#1A1A1A] hover:bg-[#FAFAFA] disabled:opacity-50"
                    >
                      {saving === `grant-${user.id}` ? 'Granting…' : 'Grant'}
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3 text-[#888888] text-xs">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="text-center py-12 text-[#888888]">No users found.</div>
        )}
      </div>
    </div>
  );
};

export default UsersAdmin;
